import React from 'react';
import { Box, Button } from '@mui/material';
import { MIN_WIDGET_HEIGHT, PRIMARY_WIDGET_COLOR } from './SingleWidgetConstants';
import { createRafResizeObserver } from '../../Utils/resizeObserver';
import { extractWidgetStructuredData, looksLikeHtmlPayload } from '../../Utils/widgetResponseUtils';
import WidgetStructuredDataRenderer from './WidgetStructuredDataRenderer';

const clampWidgetHeight = (value) => Math.max(MIN_WIDGET_HEIGHT, value);
const clampScale = (value) => Math.min(Math.max(value, 0.1), 3);
const MEASURE_THROTTLE_MS = 120;
const IFRAME_HEIGHT_BUFFER_PX = 8;
const MAX_REPORTED_IFRAME_HEIGHT_PX = 50000;
const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const SingleWidgetAnswerRenderer = ({
  answer,
  widgetId,
  isMaximized,
  shouldAutoExpand,
  scaleToFitContainer = false,
  onHeightMeasured,
  remeasureSignal = 0,
  t
}) => {
  const [iframeContentHeight, setIframeContentHeight] = React.useState(null);
  const iframeElementRef = React.useRef(null);
  const iframeResizeObserverRef = React.useRef(null);
  const iframeWidthChangeTimeoutRef = React.useRef(null);
  const iframeSyncTimeoutRef = React.useRef(null);
  const iframeLoadSettleRef = React.useRef(null);
  const lastSyncAtRef = React.useRef(0);
  const lastObservedWidthRef = React.useRef(null);
  const lastIframeHeightRef = React.useRef(null);
  const iframeMessageToken = React.useMemo(
    () => `${String(widgetId || 'widget')}:${Math.random().toString(36).slice(2)}`,
    [widgetId]
  );
  const normalizedAnswerFormat = React.useMemo(
    () => {
      return String(answer?.type || 'text').toLowerCase();
    },
    [answer?.type]
  );
  const structuredData = React.useMemo(
    () => extractWidgetStructuredData(answer, {
      includeDataFields: normalizedAnswerFormat === 'html-with-js'
    }),
    [answer, normalizedAnswerFormat]
  );
  const [showStructuredData, setShowStructuredData] = React.useState(false);
  const hasHtmlVisual = React.useMemo(
    () => looksLikeHtmlPayload(answer?.answer),
    [answer?.answer]
  );
  const isPlainTextFormat = normalizedAnswerFormat !== 'html-with-js';
  const isContentDrivenHeightFormat = normalizedAnswerFormat === 'html-with-js' || isPlainTextFormat;
  // Keep scale-to-fit disabled for content-driven height formats so widget height
  // can shrink/grow with actual content when width changes.
  const enableScaleToFit = scaleToFitContainer && !isContentDrivenHeightFormat;
  const iframeOverflowMode = isMaximized
    ? 'auto'
    : isContentDrivenHeightFormat
      ? 'hidden'
      : 'auto';

  const disconnectIframeObserver = React.useCallback(() => {
    if (iframeWidthChangeTimeoutRef.current !== null) {
      clearTimeout(iframeWidthChangeTimeoutRef.current);
      iframeWidthChangeTimeoutRef.current = null;
    }
    if (iframeSyncTimeoutRef.current !== null) {
      clearTimeout(iframeSyncTimeoutRef.current);
      iframeSyncTimeoutRef.current = null;
    }
    if (iframeResizeObserverRef.current) {
      iframeResizeObserverRef.current.disconnect();
      iframeResizeObserverRef.current = null;
    }
  }, []);

  const measureDocumentHeight = React.useCallback((doc) => {
    if (!doc) {
      return null;
    }
    const body = doc.body;
    const html = doc.documentElement;
    if (!body || !html) {
      return null;
    }
    const scaleRoot = doc.getElementById('widget-scale-root');
    const rootHeight = scaleRoot
      ? Math.max(
        scaleRoot.scrollHeight || 0,
        scaleRoot.offsetHeight || 0,
        scaleRoot.getBoundingClientRect?.().height || 0
      )
      : 0;
    if (Number.isFinite(rootHeight) && rootHeight > 0) {
      return rootHeight;
    }
    const height = Math.max(
      body.scrollHeight || 0,
      html.scrollHeight || 0
    );
    return Number.isFinite(height) ? height : null;
  }, []);

  const measureIframeHeight = React.useCallback((iframe) => {
    if (!iframe) {
      return null;
    }
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      return measureDocumentHeight(doc);
    } catch (error) {
      return null;
    }
  }, [measureDocumentHeight]);

  const ensureScaleRoot = React.useCallback((doc) => {
    if (!doc || !doc.body) {
      return null;
    }
    let root = doc.getElementById('widget-scale-root');
    if (!root) {
      root = doc.createElement('div');
      root.id = 'widget-scale-root';
      root.style.display = 'block';
      root.style.width = '100%';
      root.style.transformOrigin = 'top left';
      while (doc.body.firstChild) {
        root.appendChild(doc.body.firstChild);
      }
      doc.body.appendChild(root);
    }
    return root;
  }, []);

  const fitContentScaleAndMeasure = React.useCallback((iframe) => {
    if (!iframe) {
      return null;
    }
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc || !doc.body || !doc.documentElement) {
        return null;
      }
      const root = ensureScaleRoot(doc);
      if (!root) {
        return null;
      }

      // Reset scale before measuring natural size.
      root.style.transform = 'none';
      root.style.width = '100%';
      const containerRect = iframe.getBoundingClientRect();
      const containerWidth = containerRect.width || iframe.clientWidth || 0;
      const containerHeight = containerRect.height || iframe.clientHeight || 0;
      if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
        return measureDocumentHeight(doc);
      }

      const overflowWidth = Math.max(
        doc.body.scrollWidth || 0,
        doc.documentElement.scrollWidth || 0,
        root.scrollWidth || 0
      );

      const naturalHeight = Math.max(
        root.scrollHeight || 0,
        root.offsetHeight || 0,
        doc.body.scrollHeight || 0,
        doc.documentElement.scrollHeight || 0
      );

      if (enableScaleToFit) {
        const baseWidth = Number.isFinite(overflowWidth) && overflowWidth > 0
          ? overflowWidth
          : containerWidth;
        const widthScale = Number.isFinite(baseWidth) && baseWidth > 0
          ? (containerWidth / baseWidth)
          : 1;
        const heightScale = Number.isFinite(containerHeight) && containerHeight > 0 && Number.isFinite(naturalHeight) && naturalHeight > 0
          ? (containerHeight / naturalHeight)
          : 1;
        const scale = clampScale(Math.min(widthScale, heightScale));
        root.style.width = `${Math.max(baseWidth, 1)}px`;
        root.style.transform = `scale(${scale})`;
        const scaledHeight = Number.isFinite(naturalHeight) && naturalHeight > 0
          ? Math.ceil(naturalHeight * scale)
          : null;
        if (Number.isFinite(scaledHeight) && scaledHeight > 0) {
          doc.body.style.height = `${scaledHeight}px`;
          return scaledHeight;
        }
        return measureDocumentHeight(doc);
      }

      // For non-scale-fit formats (e.g. plaintext/text), keep natural content size
      // so measured height matches full visible content without shrinking.
      doc.body.style.height = 'auto';
      return measureDocumentHeight(doc);
    } catch (error) {
      return null;
    }
  }, [enableScaleToFit, ensureScaleRoot, measureDocumentHeight]);

  const resizeNestedIframes = React.useCallback((iframe) => {
    if (!iframe || !shouldAutoExpand) {
      return;
    }
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) {
        return;
      }
      const nestedFrames = doc.querySelectorAll('iframe');
      if (!nestedFrames.length) {
        return;
      }
      nestedFrames.forEach((nested) => {
        try {
          const nestedDoc = nested.contentDocument || nested.contentWindow?.document;
          const nestedHeight = measureDocumentHeight(nestedDoc);
          if (nestedHeight) {
            nested.style.height = `${nestedHeight}px`;
          }
        } catch (error) {
          // Cross-origin nested iframe, ignore
        }
      });
    } catch (error) {
      // Ignore access errors
    }
  }, [measureDocumentHeight, shouldAutoExpand]);

  const syncIframeHeight = React.useCallback((iframe) => {
    if (!iframe || !shouldAutoExpand) {
      return;
    }
    iframe.contentWindow?.postMessage({
      type: 'dataunfold-widget-measure',
      token: iframeMessageToken,
    }, '*');
    resizeNestedIframes(iframe);
    const measured = fitContentScaleAndMeasure(iframe) || measureIframeHeight(iframe);
    if (!measured) {
      return;
    }
    const clamped = clampWidgetHeight(Math.ceil(measured) + IFRAME_HEIGHT_BUFFER_PX);
    if (lastIframeHeightRef.current !== null && Math.abs(lastIframeHeightRef.current - clamped) < 2) {
      return;
    }
    if (lastIframeHeightRef.current !== clamped) {
      lastIframeHeightRef.current = clamped;
      setIframeContentHeight(clamped);
      if (!enableScaleToFit && typeof onHeightMeasured === 'function') {
        onHeightMeasured(clamped);
      }
    }
  }, [enableScaleToFit, fitContentScaleAndMeasure, iframeMessageToken, measureIframeHeight, onHeightMeasured, resizeNestedIframes, shouldAutoExpand]);

  const scheduleIframeHeightSync = React.useCallback((iframe) => {
    if (!iframe || !shouldAutoExpand) {
      return;
    }
    const runSync = () => {
      lastSyncAtRef.current = Date.now();
      syncIframeHeight(iframe);
    };

    const now = Date.now();
    const elapsed = now - lastSyncAtRef.current;
    if (elapsed >= MEASURE_THROTTLE_MS) {
      runSync();
      return;
    }

    if (iframeSyncTimeoutRef.current !== null) {
      clearTimeout(iframeSyncTimeoutRef.current);
    }
    iframeSyncTimeoutRef.current = setTimeout(() => {
      iframeSyncTimeoutRef.current = null;
      runSync();
    }, Math.max(16, MEASURE_THROTTLE_MS - elapsed));
  }, [shouldAutoExpand, syncIframeHeight]);

  const attachIframeResizeObserver = React.useCallback((iframe) => {
    if (!iframe || !shouldAutoExpand) {
      return;
    }
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    if (iframeResizeObserverRef.current) {
      iframeResizeObserverRef.current.disconnect();
      iframeResizeObserverRef.current = null;
    }
    lastObservedWidthRef.current = iframe.getBoundingClientRect().width || null;
    const observer = createRafResizeObserver(() => {
      const nextWidth = iframe.getBoundingClientRect().width || null;
      if (!Number.isFinite(nextWidth)) {
        return;
      }
      if (
        Number.isFinite(lastObservedWidthRef.current)
        && Math.abs(lastObservedWidthRef.current - nextWidth) < 1
      ) {
        return;
      }
      lastObservedWidthRef.current = nextWidth;
      if (iframeWidthChangeTimeoutRef.current !== null) {
        clearTimeout(iframeWidthChangeTimeoutRef.current);
      }
      iframeWidthChangeTimeoutRef.current = setTimeout(() => {
        iframeWidthChangeTimeoutRef.current = null;
        iframe.contentWindow?.postMessage({
          type: 'dataunfold-widget-measure',
          token: iframeMessageToken,
        }, '*');
      }, MEASURE_THROTTLE_MS);
    });

    observer.observe(iframe);

    iframeResizeObserverRef.current = observer;
  }, [iframeMessageToken, shouldAutoExpand]);

  React.useEffect(() => {
    const handleWidgetMessage = (event) => {
      const iframe = iframeElementRef.current;
      const payload = event.data;
      if (
        !iframe
        || event.source !== iframe.contentWindow
        || !payload
        || payload.type !== 'dataunfold-widget-height'
        || payload.token !== iframeMessageToken
        || !shouldAutoExpand
      ) {
        return;
      }
      const reportedHeight = Number(payload.height);
      if (!Number.isFinite(reportedHeight) || reportedHeight <= 0) {
        return;
      }
      const clamped = clampWidgetHeight(
        Math.min(reportedHeight + IFRAME_HEIGHT_BUFFER_PX, MAX_REPORTED_IFRAME_HEIGHT_PX)
      );
      if (lastIframeHeightRef.current === clamped) {
        return;
      }
      lastIframeHeightRef.current = clamped;
      setIframeContentHeight(clamped);
      if (typeof onHeightMeasured === 'function') {
        onHeightMeasured(clamped);
      }
    };
    window.addEventListener('message', handleWidgetMessage);
    return () => window.removeEventListener('message', handleWidgetMessage);
  }, [iframeMessageToken, onHeightMeasured, shouldAutoExpand]);

  React.useEffect(() => {
    lastIframeHeightRef.current = null;
    lastObservedWidthRef.current = null;
    lastSyncAtRef.current = 0;
    setIframeContentHeight(null);
    disconnectIframeObserver();
  }, [disconnectIframeObserver, widgetId, answer?.answer, answer?.data_json, answer?.data, normalizedAnswerFormat]);

  React.useEffect(() => {
    setShowStructuredData(false);
  }, [widgetId, answer?.answer, answer?.data_json, answer?.data, normalizedAnswerFormat]);

  React.useEffect(() => {
    if (showStructuredData) {
      iframeElementRef.current = null;
      disconnectIframeObserver();
    }
  }, [disconnectIframeObserver, showStructuredData]);

  React.useEffect(() => () => {
    disconnectIframeObserver();
  }, [disconnectIframeObserver]);

  React.useEffect(() => {
    if (!shouldAutoExpand) {
      disconnectIframeObserver();
      return;
    }
    const iframe = iframeElementRef.current;
    if (iframe) {
      disconnectIframeObserver();
      scheduleIframeHeightSync(iframe);
      attachIframeResizeObserver(iframe);
    }
  }, [attachIframeResizeObserver, disconnectIframeObserver, scheduleIframeHeightSync, shouldAutoExpand]);

  React.useEffect(() => {
    if (!shouldAutoExpand) {
      return;
    }
    const iframe = iframeElementRef.current;
    if (!iframe) {
      return;
    }
    // Force a fresh measure after grid resize release so wrapped content can grow height.
    // Reset last measured value so onHeightMeasured can fire even if measured
    // content height matches a previous value (user may have manually shrunk).
    lastIframeHeightRef.current = null;
    scheduleIframeHeightSync(iframe);
    const settleTimerId = setTimeout(() => {
      if (!iframeElementRef.current) {
        return;
      }
      // Run a second pass after layout settles to avoid stale oversized heights.
      lastIframeHeightRef.current = null;
      scheduleIframeHeightSync(iframeElementRef.current);
    }, 220);
    return () => {
      clearTimeout(settleTimerId);
    };
  }, [remeasureSignal, scheduleIframeHeightSync, shouldAutoExpand]);

  const text = answer?.answer || '';
  const format = normalizedAnswerFormat;

  const createIframeContent = React.useCallback((htmlContent) => {
    const messageTokenLiteral = JSON.stringify(iframeMessageToken);
    const htmlDocument = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            font-size: 10px;
            line-height: 1.5;
            color: #333;
            background: #ffffff;
            width: 100%;
            height: auto;
            min-height: 0;
            padding: 0;
            overflow-wrap: anywhere;
            word-break: break-word;
            overflow: ${iframeOverflowMode};
            margin: 0;
          }
          body {
            display: block;
            width: 100%;
            height: auto;
            padding: 4px;
            margin: 0;
            overflow: ${iframeOverflowMode};
            box-sizing: border-box;
          }
          @media (min-width: 768px) {
            body {
              padding: 0px;
            }
          }
          svg, canvas, iframe, video, embed, object { width: 100%; max-width: 100%; height: auto; display: block; margin: 12px auto; border-radius: 8px; background: #ffffff; }
          svg { shape-rendering: geometricPrecision; text-rendering: optimizeLegibility; image-rendering: optimizeQuality; filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08)); transition: filter 0.3s ease; }
          svg:hover { filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.12)); }
          .chart-container, [class*="chart"], [id*="chart"] { position: relative; width: 100%; margin: 12px 0; padding: 8px; background: #fafbfc; border-radius: 10px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06); }
          canvas { border-radius: 6px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06); }
          img { max-width: 100%; height: auto; display: block; margin: 8px 0; }
          table { width: 100%; max-width: 100%; table-layout: fixed; border-collapse: collapse; margin: 12px 0; font-size: 0.95em; }
          th { background: #f8f9fa; color: #333; font-weight: 600; text-align: left; padding: 8px 10px; border-bottom: 2px solid #dee2e6; }
          td { padding: 8px 10px; border-bottom: 1px solid #e9ecef; overflow-wrap: anywhere; word-break: break-word; }
          tr:last-child td { border-bottom: none; }
          tr:hover td { background: #f8f9fa; }
          pre { background: #f5f5f5; border: 1px solid #e0e0e0; border-left: 3px solid #666; padding: 12px; border-radius: 4px; overflow: hidden; white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word; margin: 12px 0; font-size: 0.9em; }
          code { font-family: 'Consolas', 'Monaco', 'Courier New', monospace; font-size: 0.9em; background: #f5f5f5; padding: 2px 5px; border-radius: 3px; }
          pre code { background: none; padding: 0; }
          h1, h2, h3, h4, h5, h6 { color: #333; font-weight: 600; margin: 14px 0 8px 0; line-height: 1.3; }
          h1 { font-size: 1.5em; } h2 { font-size: 1.3em; } h3 { font-size: 1.15em; } h4 { font-size: 1.05em; }
          p { margin: 1px 0; }
          ul, ol { margin: 4px 0; padding-left: 24px; }
          li { margin: 0px 0; }
          a { color: #0066cc; text-decoration: none; }
          a:hover { text-decoration: underline; }
          blockquote { border-left: 3px solid #ccc; padding-left: 14px; margin: 10px 0; color: #666; font-style: italic; }
          * { max-width: 100%; overflow-wrap: anywhere; word-break: break-word; }
        </style>
      </head>
      <body>
        <div id="widget-scale-root" style="display: flow-root; width: 100%; height: auto; min-height: 0;">
          ${htmlContent}
        </div>
        <script>
          (() => {
            const token = ${messageTokenLiteral};
            let pending = false;
            const reportHeight = () => {
              pending = false;
              const body = document.body;
              const content = document.getElementById('widget-scale-root');
              if (!body || !content) return;
              const style = getComputedStyle(body);
              // documentElement.scrollHeight includes the iframe viewport. Feeding
              // it back with the parent buffer grows the frame on every measure.
              const height = Math.ceil(Math.max(
                content.scrollHeight,
                content.offsetHeight,
                content.getBoundingClientRect().height
              ) + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0));
              parent.postMessage({ type: 'dataunfold-widget-height', token, height }, '*');
            };
            const schedule = () => {
              if (pending) return;
              pending = true;
              requestAnimationFrame(reportHeight);
            };
            window.addEventListener('message', (event) => {
              if (event.data?.type === 'dataunfold-widget-measure' && event.data?.token === token) {
                schedule();
              }
            });
            window.addEventListener('load', schedule);
            if (typeof ResizeObserver === 'function') {
              new ResizeObserver(schedule).observe(document.getElementById('widget-scale-root'));
            }
            schedule();
          })();
        </script>
      </body>
      </html>
    `;
    const blob = new Blob([htmlDocument], { type: 'text/html' });
    return URL.createObjectURL(blob);
  }, [iframeMessageToken, iframeOverflowMode]);

  const iframeHtmlContent = React.useMemo(() => {
    if (format === 'html-with-js') {
      return text || '';
    }
    return `
      <style>
        html, body {
          background: transparent !important;
          padding: 0 !important;
          margin: 0 !important;
          min-height: 0 !important;
          overflow: ${iframeOverflowMode} !important;
        }
        .widget-plain-text {
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          white-space: pre-wrap !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
          font-family: inherit !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          color: #333 !important;
          display: block !important;
          width: 100% !important;
        }
      </style>
      <div class="widget-plain-text">${escapeHtml(text || '')}</div>
    `;
  }, [format, iframeOverflowMode, text]);

  const iframeSrc = React.useMemo(() => createIframeContent(iframeHtmlContent), [createIframeContent, iframeHtmlContent]);

  React.useEffect(() => () => {
    if (iframeSrc) {
      URL.revokeObjectURL(iframeSrc);
    }
  }, [iframeSrc]);

  if (!answer || answer.error) {
    return null;
  }

  const getIframeHeight = () => {
    if (enableScaleToFit) {
      return '100%';
    }
    if (shouldAutoExpand) {
      return `${iframeContentHeight || MIN_WIDGET_HEIGHT}px`;
    }
    return '100%';
  };

  const commonIframeStyle = {
    width: '100%',
    height: getIframeHeight(),
    minHeight: (shouldAutoExpand && !enableScaleToFit) ? `${MIN_WIDGET_HEIGHT}px` : 0,
    border: 'none',
    backgroundColor: 'transparent',
    overflow: iframeOverflowMode,
    display: 'block',
    flex: isMaximized ? '1 1 auto' : 'initial'
  };

  const hasStructuredData = structuredData !== null;
  const canToggleVisualData = format === 'html-with-js' && hasHtmlVisual && hasStructuredData;
  const shouldRenderStructuredData = hasStructuredData && !canToggleVisualData;

  const handleIframeLoad = (e) => {
    const iframe = e.target;
    iframeElementRef.current = iframe;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc && doc.body) doc.body.style.overflow = iframeOverflowMode;
      if (doc && doc.documentElement) doc.documentElement.style.overflow = iframeOverflowMode;
    } catch (error) {}
    if (shouldAutoExpand) {
      scheduleIframeHeightSync(iframe);
      attachIframeResizeObserver(iframe);
      // Delayed re-measure to catch late-loading content (images, fonts)
      if (iframeLoadSettleRef.current !== null) {
        clearTimeout(iframeLoadSettleRef.current);
      }
      iframeLoadSettleRef.current = setTimeout(() => {
        iframeLoadSettleRef.current = null;
        if (iframeElementRef.current === iframe) {
          lastIframeHeightRef.current = null;
          scheduleIframeHeightSync(iframe);
        }
      }, 400);
      // Track image loads inside the iframe to re-measure when they complete
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          const images = iframeDoc.querySelectorAll('img');
          images.forEach((img) => {
            if (!img.complete) {
              img.addEventListener('load', () => {
                if (iframeElementRef.current === iframe) {
                  lastIframeHeightRef.current = null;
                  scheduleIframeHeightSync(iframe);
                }
              }, { once: true });
            }
          });
        }
      } catch (imgErr) {}
    }
  };

  if (shouldRenderStructuredData) {
    return (
      <WidgetStructuredDataRenderer
        data={structuredData}
        isMaximized={isMaximized}
        t={t}
      />
    );
  }

  const iframe = (
    <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', width: '100%', height: isMaximized ? '100%' : 'auto' }}>
      <iframe
        src={iframeSrc}
        ref={(node) => { iframeElementRef.current = node; }}
        style={commonIframeStyle}
        onLoad={handleIframeLoad}
        sandbox="allow-scripts allow-forms"
        title={format === 'html-with-js' ? 'JavaScript Widget Content' : 'Text Widget Content'}
        key={format === 'html-with-js' ? `widget-js-${widgetId}-${isMaximized ? 'maximized' : 'normal'}` : undefined}
      />
    </Box>
  );

  if (!canToggleVisualData) {
    return iframe;
  }

  const visualDataLabel = showStructuredData
    ? (typeof t === 'function' ? t('widget.view_visual', { defaultValue: 'View visual' }) : 'View visual')
    : (typeof t === 'function' ? t('widget.view_data', { defaultValue: 'View data' }) : 'View data');

  return (
    <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%', height: isMaximized ? '100%' : 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0, mb: 0.5 }}>
        <Button
          type="button"
          size="small"
          variant="outlined"
          onClick={() => setShowStructuredData((previousValue) => !previousValue)}
          sx={{
            minHeight: 28,
            px: 1,
            py: 0.25,
            borderColor: `${PRIMARY_WIDGET_COLOR}60`,
            color: PRIMARY_WIDGET_COLOR,
            textTransform: 'none',
            fontSize: '0.72rem',
            '&:hover': {
              borderColor: PRIMARY_WIDGET_COLOR,
              backgroundColor: `${PRIMARY_WIDGET_COLOR}08`
            }
          }}
        >
          {visualDataLabel}
        </Button>
      </Box>
      {showStructuredData ? (
        <WidgetStructuredDataRenderer
          data={structuredData}
          isMaximized={isMaximized}
          t={t}
        />
      ) : iframe}
    </Box>
  );
};

export default SingleWidgetAnswerRenderer;
